import { useState } from 'react';

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || '';
const BACKEND_URL   = process.env.REACT_APP_BACKEND_URL   || 'http://localhost:5000';

// ─── Composant changement de mot de passe ─────────────────────────────────────
// Utilise Supabase Auth — plus de PBKDF2 côté client.
//
// Deux modes :
//   1. Salarié change son propre mot de passe (vérifie l'ancien via re-auth)
//   2. Admin réinitialise le mot de passe de quelqu'un (envoie un email Supabase)
//
// Props :
//   getAccessToken()  → string  — depuis useAuth (token JWT courant)
//   targetUser        — utilisateur cible (profil normalisé)
//   isAdmin           — si true, mode réinitialisation admin
//   onClose()         — callback fermeture

export function ChangerMotDePasse({ getAccessToken, prolongerSession, targetUser, isAdmin = false, onClose, user }) {
  const [nouveau,  setNouveau]  = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [erreur,   setErreur]   = useState('');
  const [succes,   setSucces]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [visible,  setVisible]  = useState(false);

  const reinitParAdmin = isAdmin && targetUser?.id !== user?.id;

  // Règles de robustesse (toutes obligatoires)
  const regles = [
    { ok: nouveau.length >= 8,                      label: '8 caractères minimum' },
    { ok: /[A-Z]/.test(nouveau),                    label: '1 majuscule' },
    { ok: /[0-9]/.test(nouveau),                    label: '1 chiffre' },
    { ok: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(nouveau), label: '1 caractère spécial' },
  ];
  const robustesse = regles.filter(r => r.ok).length;
  const mdpValide  = robustesse === 4; // toutes les règles doivent être satisfaites
  const couleurBarre = robustesse <= 1 ? '#dc2626' : robustesse === 2 ? '#f59e0b' : robustesse === 3 ? '#3b82f6' : '#22c55e';

  // ── Mode admin : envoyer un email de reset via le backend (service_role) ───────────
  const envoyerResetAdmin = async () => {
    if (!targetUser?.email) {
      setErreur('Aucun email associé à ce compte. Impossible d\'envoyer le lien de réinitialisation.');
      return;
    }
    setLoading(true);
    setErreur('');
    try {
      // On passe par le backend (service_role) pour garantir l'envoi,
      // même si l'email dans cmob_profiles diffère de celui dans Supabase Auth.
      const r = await fetch(`${BACKEND_URL}/api/auth/send-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId:     targetUser.id,
          email:      targetUser.email,
          redirectTo: window.location.origin,
        }),
      });
      if (r.ok || r.status === 200) {
        setSucces(true);
      } else {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
    } catch (e) {
      setErreur('Erreur lors de l\'envoi : ' + e.message);
    }
    setLoading(false);
  };

  // ── Mode salarié : changer son propre mot de passe via JWT ───────────────
  const valider = async () => {
    setErreur('');
    if (nouveau.length < 8)  { setErreur('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    if (!/[A-Z]/.test(nouveau)) { setErreur('Le mot de passe doit contenir au moins une majuscule.'); return; }
    if (!/[0-9]/.test(nouveau)) { setErreur('Le mot de passe doit contenir au moins un chiffre.'); return; }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(nouveau)) { setErreur('Le mot de passe doit contenir au moins un caractère spécial.'); return; }
    if (nouveau !== confirm)  { setErreur('Les deux mots de passe ne correspondent pas.'); return; }
    if (nouveau === targetUser?.login) { setErreur('Le mot de passe ne peut pas être identique au login.'); return; }

    setLoading(true);
    try {
      // ── Récupération du token — prop ou fallback localStorage ──────────────
      let accessToken = getAccessToken?.();
      if (!accessToken) {
        // Fallback : lire directement la session stockée (même clé que useAuth)
        try {
          const raw = localStorage.getItem('cmob_supabase_session');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.access_token && parsed?.expires_at > Date.now()) {
              accessToken = parsed.access_token;
            }
          }
        } catch {}
      }
      if (!accessToken) {
        await prolongerSession?.();
        accessToken = getAccessToken?.();
      }
      if (!accessToken) { setErreur('Session expirée. Reconnectez-vous.'); setLoading(false); return; }

      // Supabase Auth : update password avec le JWT courant
      const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          apikey:        SUPABASE_ANON,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: nouveau }),
      });

      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const msg = err.message || err.error_description || '';
        if (r.status === 422) {
          // Supabase renvoie 422 uniquement quand le nouveau mdp = l'ancien
          throw new Error("Le nouveau mot de passe doit être différent de l'ancien.");
        }
        if (r.status === 401) {
          throw new Error('Session expirée. Reconnectez-vous et réessayez.');
        }
        throw new Error(msg || `Erreur HTTP ${r.status}`);
      }
      setSucces(true);
      setTimeout(() => onClose?.(), 1800);
    } catch (e) {
      setErreur('Erreur : ' + e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ padding: '4px 0' }}>
      {succes ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>✅</div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--success)' }}>
            {reinitParAdmin
              ? `Email de réinitialisation envoyé à ${targetUser?.email}`
              : 'Mot de passe changé avec succès'}
          </div>
          {reinitParAdmin && (
            <div style={{ fontSize: '0.82rem', color: 'var(--muted)', marginTop: 8 }}>
              L'utilisateur recevra un lien valable 1 heure.
            </div>
          )}
        </div>
      ) : reinitParAdmin ? (
        // ── Mode admin : bouton d'envoi email ────────────────────────────────
        <>
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8,
            padding: '8px 12px', marginBottom: 16, fontSize: '0.82rem', color: '#92400e' }}>
            ⚠️ Réinitialisation admin — Un email sera envoyé à <strong>{targetUser?.email || '(email manquant)'}</strong>.
            L'utilisateur cliquera sur le lien pour définir un nouveau mot de passe.
          </div>
          {erreur && (
            <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8,
              padding: '9px 13px', marginBottom: 14, fontSize: '0.82rem', fontWeight: 600 }}>
              ❌ {erreur}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose}
              style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid var(--border)',
                background: 'var(--bg)', cursor: 'pointer', fontFamily: 'inherit',
                fontWeight: 700, fontSize: '0.88rem', color: 'var(--text)' }}>
              Annuler
            </button>
            <button onClick={envoyerResetAdmin} disabled={loading || !targetUser?.email}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none',
                background: loading || !targetUser?.email ? 'var(--border)' : 'var(--bleu)',
                color: '#fff', cursor: loading || !targetUser?.email ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontWeight: 700, fontSize: '0.88rem' }}>
              {loading ? '⏳ Envoi…' : '📧 Envoyer le lien de réinitialisation'}
            </button>
          </div>
        </>
      ) : (
        // ── Mode salarié : saisir le nouveau mot de passe ────────────────────
        <>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--bleu)',
              textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>
              Nouveau mot de passe *
            </label>
            <div style={{ position: 'relative' }}>
              <input type={visible ? 'text' : 'password'} value={nouveau}
                onChange={e => setNouveau(e.target.value)}
                placeholder="Nouveau mot de passe"
                style={{ width: '100%', padding: '9px 40px 9px 12px', borderRadius: 8,
                  border: '1.5px solid var(--border)', background: 'var(--bg)',
                  fontFamily: 'inherit', fontSize: '0.88rem', boxSizing: 'border-box' }}
              />
              <button onClick={() => setVisible(v => !v)} tabIndex={-1}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: 'var(--muted)' }}>
                {visible ? '🙈' : '👁️'}
              </button>
            </div>
            {nouveau && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
                  {[1,2,3,4].map(i => (
                    <div key={i} style={{ flex: 1, height: 4, borderRadius: 2,
                      background: i <= robustesse ? couleurBarre : 'var(--border)',
                      transition: 'background 0.2s' }}/>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {regles.map(({ ok, label }) => (
                    <span key={label} style={{ fontSize: '0.7rem', color: ok ? '#22c55e' : 'var(--muted)' }}>
                      {ok ? '✓' : '○'} {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--bleu)',
              textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>
              Confirmer le nouveau mot de passe *
            </label>
            <input type={visible ? 'text' : 'password'} value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Répéter le nouveau mot de passe"
              onKeyDown={e => e.key === 'Enter' && valider()}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8,
                border: `1.5px solid ${confirm && nouveau !== confirm ? '#dc2626' : 'var(--border)'}`,
                background: 'var(--bg)', fontFamily: 'inherit', fontSize: '0.88rem', boxSizing: 'border-box' }}
            />
            {confirm && nouveau !== confirm && (
              <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: 4 }}>
                Les mots de passe ne correspondent pas
              </div>
            )}
          </div>

          {erreur && (
            <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8,
              padding: '9px 13px', marginBottom: 14, fontSize: '0.82rem', fontWeight: 600 }}>
              ❌ {erreur}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose}
              style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid var(--border)',
                background: 'var(--bg)', cursor: 'pointer', fontFamily: 'inherit',
                fontWeight: 700, fontSize: '0.88rem', color: 'var(--text)' }}>
              Annuler
            </button>
            <button onClick={valider}
              disabled={loading || !nouveau || !confirm || nouveau !== confirm || !mdpValide}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none',
                background: loading || !nouveau || !confirm || nouveau !== confirm || !mdpValide
                  ? 'var(--border)' : 'var(--bleu)',
                color: '#fff', fontFamily: 'inherit', fontWeight: 700, fontSize: '0.88rem',
                cursor: 'pointer' }}>
              {loading ? '⏳ Enregistrement…' : '🔐 Changer'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
